// Resumo (TLDR) da IA com o estado da análise e "analisar de novo" (POST /v1/cards/:id/reprocess).
import { useState } from "preact/hooks";
import type { Card, IngestState } from "../../../../shared/types.ts";
import { Ingest } from "../Grid.tsx";
import { IconSpin } from "../Icons.tsx";
import { ingestReason, quietIngest } from "../lib.ts";
import { store, waitsForAi } from "../state.ts";
import { IconRedo } from "./icons.tsx";

/** Tipos que sempre passam pela análise (nota só quando é longa). */
const ANALYZED = new Set<Card["kind"]>(["image", "link", "pdf", "video", "audio", "file"]);
const BUSY: Partial<Record<IngestState, string>> = { uploading: "Enviando o arquivo…", queued: "Na fila para análise…", processing: "Analisando…" };

export function Summary({ card: c }: { card: Card }) {
  // rev de quando pedimos: até o pull trazer o card em "queued" (rev novo), o botão fica ocupado.
  const [askedAt, setAskedAt] = useState<number | null>(null);
  if (c.ingestState === "failed") return <Ingest card={c} />;
  // Sem a chave da IA (09-PARIDADE §6.1): o resumo da própria página (og:description, JSON-LD) aparece; sem ele, uma
  // linha calma. Nem selo "Parcial" nem "analisar de novo", que só repetiria a mesma análise sem IA. Com a chave ligada
  // depois, o card guardado sem ela segue abaixo: "Ainda sem resumo" e "analisar de novo", sem selo.
  if (waitsForAi(c)) {
    return (
      <section class="summary rd-summary is-quiet" aria-labelledby="summary-label">
        <h3 class="section-label" id="summary-label">
          Resumo
        </h3>
        {c.summary ? <p>{c.summary}</p> : <p class="rd-dim">Resumo e etiquetas automáticas chegam quando a análise automática estiver ligada.</p>}
      </section>
    );
  }
  const asked = askedAt === c.rev;
  const busy = BUSY[c.ingestState] ?? (asked ? "Pedindo uma nova análise…" : null);
  const partial = c.ingestState === "partial" && !quietIngest(c);
  if (!c.summary && !busy && !partial && !ANALYZED.has(c.kind)) return null;
  const again = () => {
    setAskedAt(c.rev);
    store.reprocess(c.id).catch(() => setAskedAt(null)); // o store já avisou o motivo
  };
  return (
    <section class="summary rd-summary" aria-labelledby="summary-label">
      <h3 class="section-label" id="summary-label">
        Resumo
      </h3>
      {c.summary ? <p>{c.summary}</p> : !busy && <p class="rd-dim">Ainda sem resumo.</p>}
      <p class={`rd-state${busy ? "" : " sr-only"}`} role="status">
        {busy && <IconSpin />}
        {busy ?? ""}
      </p>
      {partial && !busy && (
        <p class="rd-state is-partial">
          <span class="rd-badge">Parcial</span>
          {ingestReason(c.ingestError)}.
        </p>
      )}
      <button type="button" class="rd-redo" aria-label="Analisar de novo" data-tip={busy ? "Analisando…" : "Analisar de novo"} disabled={!!busy} onClick={again}>
        <IconRedo />
      </button>
    </section>
  );
}
